import React from 'react'
import PropTypes from 'prop-types'
import { size, map, join, isEqual, identity, find, get, isEmpty, isUndefined, sortBy, reverse } from 'lodash'

import { makeStyles } from '@material-ui/core/styles'
import { Box, Typography, Paper } from '@material-ui/core'

import {
  getSegmentValue,
  getSegmentOperation,
  getIsUsingManualStudentSelection,
  getIsUsingMyMatches,
} from 'components/SegmentSelector/segment-service'
import {
  ONE_TIME_TRIGGER,
  AUTOMATED_TRIGGER_CONFIGS,
} from 'components/SegmentSelector/outreach-message-trigger-constants'
import { getUserTypeDisplay } from 'components/SegmentSelector/UserTypeSelector'
import { getLabelByIncomeDecile } from 'components/SegmentSelector/DemographicsSelector/NeighborhoodIncomeSelector'

const DEFAULT_GRAD_YEARS = [0, 1, 2, 3]
const DEFAULT_ENGAGEMENTS = 'Following, Applying, Applied'
const CLASS_YEAR_CONFIG = {
  3: 'Freshmen',
  2: 'Sophomores',
  1: 'Juniors',
  0: 'Seniors',
}
const getLegalSexLabel = (value) => {
  switch (value) {
    case 'male':
      return 'Male'
    case 'female':
      return 'Female'
    default:
      return 'Everyone'
  }
}
const getCommunityTypeLabel = (value) => {
  switch (value) {
    case 'city':
      return 'City'
    case 'suburban':
      return 'Suburban'
    case 'town':
      return 'Town'
    case 'rural':
      return 'Rural'
    default:
      return ''
  }
}
const getNeighborhoodIncomeLabel = (value) => {
  const from = value[0] - 1 // subtract 1 to the from value to get the correct label
  const to = value[1]
  const fromLabel = getLabelByIncomeDecile(from)
  const toLabel = getLabelByIncomeDecile(to)
  return `Min Median: ${fromLabel} to Max Median: ${toLabel}`
}

const arrayToString = (arr, separator = ', ') => join(arr, separator)

const getSegmentInfo = (segment, type, segmentName) => {
  const values = getSegmentValue(segment, type, segmentName)
  const operation = getSegmentOperation(segment, type, segmentName)
  const hasValues = size(values) > 0
  return [hasValues, values, operation]
}
const getEngagementInfo = (segment) => {
  let engagement = DEFAULT_ENGAGEMENTS
  if (!isUndefined(segment)) {
    engagement = arrayToString(getSegmentInfo(segment, 'Must', 'EngagementStage')[1])
  }
  return engagement
}

const useStyles = makeStyles({
  value: {
    width: '252px',
  },
})

const AudienceDetails = ({ message }) => {
  const classes = useStyles()
  const segment = message.Segment
  const [hasInterests, interests] = getSegmentInfo(segment, 'OneOf', 'Interests')
  const [hasAcademicPrefs, academicPrefs] = getSegmentInfo(segment, 'OneOf', 'AcademicFocus')
  const [hasCareers, careers] = getSegmentInfo(segment, 'Must', 'Careers')
  const [hasSatScores, satScores] = getSegmentInfo(segment, 'OneOf', 'SAT')
  const [hasActScores, actScores] = getSegmentInfo(segment, 'OneOf', 'ACT')
  const [hasPactScores, pactScores] = getSegmentInfo(segment, 'OneOf', 'PACT')
  const [hasPsatNmsqtScores, psatNmsqtScores] = getSegmentInfo(segment, 'OneOf', 'PSATNMSQT')
  const [hasPsat10Scores, psat10Scores] = getSegmentInfo(segment, 'OneOf', 'PSAT10')
  const [hasPsat89Scores, psat89Scores] = getSegmentInfo(segment, 'OneOf', 'PSAT89')
  const [hasNeighborhoodIncome, neighborhoodIncome] = getSegmentInfo(segment, 'Must', 'NeighborhoodIncome')
  const [hasCommunityType, communityType] = getSegmentInfo(segment, 'Must', 'NeighborhoodType')
  const [hasState, states] = getSegmentInfo(segment, 'Must', 'USAState')
  const [hasHighSchool, highschools] = getSegmentInfo(segment, 'Must', 'HighSchool')
  const [hasIsPrivate, isPrivate] = getSegmentInfo(segment, 'Must', 'IsPrivate')
  const hasReducedLunchValue = getSegmentValue(segment, 'Must', 'HasReducedLunch')
  const legalSex = getSegmentValue(segment, 'Must', 'LegalSex')

   
  const [hasFirstGen, , firstGenOperation] = getSegmentInfo(segment, 'Must', 'FirstGeneration')
  const firstGen = firstGenOperation === 'in'

  let gradYears = getSegmentInfo(segment, 'Must', 'CurrentGradYearOffset')[1]
  if (isEmpty(gradYears)) {
    gradYears = DEFAULT_GRAD_YEARS
  }

  gradYears = reverse(sortBy(gradYears, identity))

  const isOneTime = isEqual(message.Trigger, ONE_TIME_TRIGGER)
  const triggers = isOneTime
    ? getEngagementInfo(segment)
    : get(find(AUTOMATED_TRIGGER_CONFIGS, { value: message.Trigger }), 'label', 'Unknown')
  const engagementHeading = isOneTime ? 'Engagement Stage' : 'Trigger'
  const isUsingManualStudentSelection = getIsUsingManualStudentSelection(segment)
  const isUsingMyMatches = getIsUsingMyMatches(segment)
  const audienceDetails = [
    {
      section: getUserTypeDisplay(message.Segment),
      values: isUsingManualStudentSelection
        ? 'Custom List of Student IDs'
        : isUsingMyMatches
          ? 'My Matches'
          : 'Followers',
      display: true,
    },
    {
      section: engagementHeading,
      values: triggers,
      display: true,
    },
    {
      section: 'Class Year',
      values: arrayToString(map(gradYears, (year) => CLASS_YEAR_CONFIG[year])),
      display: true,
    },
    {
      section: 'Academic Focus',
      values: arrayToString(academicPrefs),
      display: hasAcademicPrefs,
    },
    {
      section: 'Student Life',
      values: arrayToString(interests),
      display: hasInterests,
    },
    {
      section: 'Career Interests',
      values: arrayToString(careers, '; '),
      display: hasCareers,
    },
    {
      section: 'First Generation',
      values: firstGen ? 'First Generation Students Only' : 'Non-First Generation Students Only',
      display: hasFirstGen,
    },
    {
      section: 'Legal Sex',
      values: getLegalSexLabel(arrayToString(legalSex)),
      display: true,
    },
    {
      section: 'Neighborhood Household Income',
      values: getNeighborhoodIncomeLabel(neighborhoodIncome),
      display: hasNeighborhoodIncome,
    },
    {
      section: 'Student High School State',
      values: arrayToString(states),
      display: hasState,
    },
    {
      section: 'Community Type',
      values: communityType.map(getCommunityTypeLabel).join(', '),
      display: hasCommunityType,
    },
    {
      section: 'Student High School',
      values: arrayToString(map(highschools, 'name')),
      display: hasHighSchool,
    },
    {
      section: 'High School Type',
      values: isPrivate[0] ? 'Private' : hasReducedLunchValue[0] ? 'Title I Eligible' : 'Public',
      display: hasIsPrivate,
    },
    {
      section: 'ACT Score Range',
      values: `${actScores[0]} - ${actScores[1]}`,
      display: hasActScores,
    },
    {
      section: 'SAT Score Range',
      values: `${satScores[0]} - ${satScores[1]}`,
      display: hasSatScores,
    },
    {
      section: 'Pre-ACT Score Range',
      values: `${pactScores[0]} - ${pactScores[1]}`,
      display: hasPactScores,
    },
    {
      section: 'PSAT/NMSQT Score Range',
      values: `${psatNmsqtScores[0]} - ${psatNmsqtScores[1]}`,
      display: hasPsatNmsqtScores,
    },
    {
      section: 'PSAT 10 Score Range',
      values: `${psat10Scores[0]} - ${psat10Scores[1]}`,
      display: hasPsat10Scores,
    },
    {
      section: 'PSAT 8/9 Score Range',
      values: `${psat89Scores[0]} - ${psat89Scores[1]}`,
      display: hasPsat89Scores,
    },
  ]
  return (
    <Paper
      elevation={0}
      component={Box}
      p={3}
      mb={3}
    >
      <Typography variant="h6">Audience</Typography>
      <div>
        {map(
          audienceDetails,
          (segmentType, i) =>
            segmentType.display && (
              <Box
                key={i}
                display="flex"
                justifyContent="space-between"
                mt={3}
              >
                <Typography variant="body1">{segmentType.section}</Typography>
                <Typography
                  variant="body1"
                  className={classes.value}
                >
                  {segmentType.values}
                </Typography>
              </Box>
            ),
        )}
      </div>
    </Paper>
  )
}
AudienceDetails.propTypes = {
  message: PropTypes.object.isRequired,
}

export default AudienceDetails
